import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Field } from 'react-final-form';
import css from './FavoritesModal.module.css';
import Modal from '../Modal/Modal';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { addItemToFavorites } from '../../containers/FavoritesPage/FavoritesPage.duck';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import { useConfiguration } from '../../context/configurationContext';
import { userSelector } from '../../ducks/user.duck';
import { retrieveUserFavoritesList } from '../../util/userDataExtraction';
import { PrimaryButton, SecondaryButton } from '../Button/Button';
import { FormattedMessage } from 'react-intl';
import { H3 } from '../Heading/Heading';
import { updateInProgressSelector } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { isArrayLength } from '../../util/genericHelpers';

const FavoritesModal = ({ isOpen, onClose, activeFavoriteId, firstImage, currentUserId }) => {
  const currentUser = useSelector(userSelector);
  const updateInProgress = useSelector(updateInProgressSelector);
  const favorites = retrieveUserFavoritesList(currentUser);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const config = useConfiguration();
  const firstImageUrl = firstImage?.attributes?.variants?.default?.url;
  // Handle form submission
  const handleSubmit = (values, form) => {
    const { categoryName } = values;

    let updatedFavorites = [...favorites];

    const existingCategory = updatedFavorites.find(category => category.name === categoryName);

    if (existingCategory) {
      // Add to existing category
      updatedFavorites = updatedFavorites.map(category =>
        category.name === categoryName
          ? { ...category, ids: [...new Set([...category.ids, activeFavoriteId])] }
          : category
      );
    } else {
      // Create new category
      updatedFavorites.push({
        coverImage:
          firstImageUrl ||
          'https://images.pexels.com/photos/248547/pexels-photo-248547.jpeg?_gl=1*15o6h3k*_ga*MTc4NzY2MTMyNi4xNzUwMTQ1Mjkz*_ga_8JE65Q40S6*czE3NTg3MTgzMjckbzE2JGcxJHQxNzU4NzE4MzU3JGozMCRsMCRoMA..', // Fallback if image is missing
        ids: [activeFavoriteId],
        name: categoryName,
      });
    }

    // Dispatch action to update favorites
    dispatch(
      addItemToFavorites({
        config,
        currentFavoriteId: activeFavoriteId,
        favorites: updatedFavorites,
        isAuthenticated,
        userId: currentUserId,
      })
    )
      .then(() => onClose()) // Close modal on success
      .catch(error => console.error('Failed to update favorites:', error))
      .finally(() => form.reset());
  };

  // Validation for form
  const validate = values => {
    const errors = {};
    if (!values.categoryName) {
      errors.categoryName = 'Please enter or select a category name';
    }
    return errors;
  };

  return (
    <Modal
      id="FavoritesModal"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={(componentId, disableScrolling) =>
        dispatch(manageDisableScrolling(componentId, disableScrolling))
      }
      modalTitle={<FormattedMessage id="FavoritesModal.title" />}
    >
      <div className={css.modalContent}>
        <Form
          onSubmit={handleSubmit}
          validate={validate}
          render={({ handleSubmit, submitting, invalid, errors, form, values }) => (
            <form className={css.favoriteModalForm} onSubmit={handleSubmit}>
              {/* Existing Categories as Cards */}
              {isArrayLength(favorites) && (
                <div className={css.categoryList}>
                  <H3 as="h3">
                    <FormattedMessage id="FavoritesModal.existingCategories" />
                  </H3>
                  {isArrayLength(favorites) ? (
                    <div className={css.categoryGrid}>
                      {favorites.map(category => (
                        <div
                          key={category.name}
                          className={`${css.categoryCard} ${
                            values.categoryName === category.name ? css.selected : ''
                          }`}
                          onClick={() => form.change('categoryName', category.name)}
                        >
                          <div className={css.categoryImage}>
                            <img src={category.coverImage} alt={category.name} />
                          </div>
                          <span className={css.categoryName}>{category.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>
                      <FormattedMessage id="FavoritesModal.noCategories" />
                    </p>
                  )}
                </div>
              )}

              {/* Category Name Input (for new or prefilled) */}
              <div className={css.newCategory}>
                <H3 as="h3">
                  <FormattedMessage id="FavoritesModal.categoryName" />
                </H3>
                <Field name="categoryName">
                  {({ input, meta }) => (
                    <div>
                      <input
                        type="text"
                        {...input}
                        placeholder="Enter category name"
                        className={css.input}
                      />
                      {meta.touched && meta.error && (
                        <span className={css.error}>{meta.error}</span>
                      )}
                    </div>
                  )}
                </Field>
              </div>

              {/* Form-level error */}
              {errors.form && <span className={css.error}>{errors.form}</span>}

              {/* Buttons */}
              <div className={css.buttons}>
                <PrimaryButton
                  type="submit"
                  disabled={submitting || invalid}
                  className={css.submitButton}
                  inProgress={updateInProgress}
                >
                  <FormattedMessage id="FavoritesModal.save" />
                </PrimaryButton>
                <SecondaryButton type="button" onClick={onClose} className={css.cancelButton}>
                  <FormattedMessage id="FavoritesModal.cancel" />
                </SecondaryButton>
              </div>
            </form>
          )}
        />
      </div>
    </Modal>
  );
};

export default FavoritesModal;
